/**
 * Formatted, paginated Compare — the MS-Word "Compare Documents" model.
 *
 * The redline is built OFFLINE by `buildRedlineDocx` (a pure function in
 * @propdocs-editor/core): parse Version A and Version B with the SAME parser,
 * align paragraphs on normalized text, and emit a complete merged .docx with
 * B's differences baked in as native tracked changes (w:ins / w:del). The
 * editor then simply MOUNTS that document read-only — the standard pipeline
 * (parseDocx → PM marks → painter + sidebar) renders the redline natively.
 *
 * There is deliberately NO post-mount mutation here. The previous design
 * applied suggestions to a live editor and needed a paragraph-count polling
 * hack (the view fires "ready" before async parse finishes) plus a re-entry
 * guard (the mutation re-fired "ready" → infinite loop). Building the document
 * before any editor exists eliminates that entire failure class, and makes
 * compare testable headlessly against the real corpus.
 *
 * The suggestions exist only inside this generated document — nothing is ever
 * persisted to `document_suggestions`.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DocxEditor } from '@propdocs-editor/react';
import '@propdocs-editor/react/styles.css';
import { buildRedlineDocx } from '@propdocs-editor/core/docx';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import type { CompareVersion } from './compareTypes';

export function CompareRedlineView({
  versions,
  onRenameVersion,
}: {
  versions: CompareVersion[];
  onRenameVersion?: (version: CompareVersion, name: string) => Promise<void>;
}) {
  // A = older/base (rendered live), B = newer (its changes shown on top).
  // Seed A = second-newest, B = current so opening shows "what changed since
  // the last version" immediately.
  const [aKey, setAKey] = useState<string>(versions[1]?.key ?? '');
  const [bKey, setBKey] = useState<string>(versions[1] ? (versions[0]?.key ?? '') : '');
  const [merged, setMerged] = useState<ArrayBuffer | null>(null);
  const [changeCount, setChangeCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mountKey, setMountKey] = useState(0);

  const a = useMemo(() => versions.find((v) => v.key === aKey), [versions, aKey]);
  const b = useMemo(() => versions.find((v) => v.key === bKey), [versions, bKey]);

  // Build the merged redline whenever the pair changes. Pure data flow: bytes
  // in, bytes out, then a fresh mount of the RESULT.
  const lastPair = useRef<string>('');
  React.useEffect(() => {
    if (!a || !b || a.key === b.key) return;
    const pair = `${a.key}→${b.key}`;
    if (lastPair.current === pair) return;
    lastPair.current = pair;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError(null);
      try {
        const [aBytes, bBytes] = await Promise.all([a.getBytes(), b.getBytes()]);
        if (!aBytes || !bBytes) throw new Error('Could not load one of the versions.');
        const { buffer, stats } = await buildRedlineDocx(aBytes, bBytes, {
          revisedLabel: b.label,
        });
        if (cancelled) return;
        setChangeCount(stats.inserted + stats.deleted + stats.amended + stats.changedTables);
        setMerged(buffer);
        setMountKey((k) => k + 1);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Compare failed.');
          setMerged(null);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [a, b]);

  // Click a card to advance the A→B selection (selecting B auto-compares).
  const selectVersion = useCallback(
    (key: string) => {
      if (!aKey || (aKey && bKey)) {
        setAKey(key);
        setBKey('');
        return;
      }
      if (key === aKey) return;
      setBKey(key);
    },
    [aKey, bKey]
  );

  return (
    <div style={PAGE}>
      <div style={BODY_ROW}>
        <VersionHistoryPanel
          versions={versions}
          baseKey={aKey}
          revKey={bKey}
          busy={busy}
          onSelect={selectVersion}
          onRename={onRenameVersion}
        />
        <div style={EDITOR_COL}>
          {error && <div style={ERROR_BOX}>{error}</div>}
          {merged && changeCount === 0 && !busy && <div style={NOTICE}>These two versions have identical content.</div>}
          {merged ? (
            <div style={EDITOR_WRAP}>
              <DocxEditor
                key={mountKey}
                documentBuffer={merged}
                mode="viewing"
                readOnly
                compareMode
                showToolbar={false}
                showRuler={false}
                showZoomControl={false}
                renderLogo={() => null}
              />
            </div>
          ) : (
            <div style={EMPTY}>
              {busy ? 'Building redline…' : 'Pick two versions from the list on the left to compare them.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PAGE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  fontFamily: 'Arial, sans-serif',
};
const BODY_ROW: React.CSSProperties = { flex: 1, minHeight: 0, display: 'flex' };
const EDITOR_COL: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  position: 'relative',
};
const EDITOR_WRAP: React.CSSProperties = { flex: 1, minHeight: 0, position: 'relative' };
const NOTICE: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 12,
  color: '#1e6b33',
  background: '#e9f7ee',
  borderBottom: '1px solid #cdebd8',
};
const ERROR_BOX: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  color: '#c5221f',
  background: '#fce8e6',
};
const EMPTY: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#5f6368',
  fontSize: 14,
  textAlign: 'center',
  padding: 24,
};
